/**
 * Error boundary component for catching React errors
 *
 * Wraps the application and catches any unhandled errors in the React tree.
 * Errors are reported to Honeybadger and a fallback UI is displayed.
 */

import React from 'react';
import { honeybadger } from '@/services/honeybadger';
import { HoneybadgerErrorBoundary } from '@honeybadger-io/react';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
}

/**
 * Fallback UI displayed when an error occurs
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-red-500 mb-4">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          We&apos;ve been notified and are working to fix the issue.
        </p>
        {error && process.env.NODE_ENV === 'development' && (
          <pre className="text-left text-sm bg-gray-100 p-4 rounded mb-4 overflow-auto">
            {error.message}
          </pre>
        )}
        <div className="space-x-4">
          {resetError && (
            <button
              onClick={resetError}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Try again
            </button>
          )}
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error boundary wrapper using Honeybadger
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return (
    <HoneybadgerErrorBoundary honeybadger={honeybadger} ErrorComponent={ErrorFallback}>
      {children}
    </HoneybadgerErrorBoundary>
  );
};

export default ErrorBoundary;
